import firebase from "js/firebase";
import { serverApi } from "js/config";

import moment from "moment";
import ApiError from "common/ApiError";

/**
 * The Api object allows us to call server api endpoint like they are function calls.
 * This reads data from the serverApi object passed in here from the server that has a list of all
 * api endpoints.
 */
const Api = {};

/**
 * Builds a request object containing "url" and "payload" fields,
 * parses the supplied url if it contains resource parameters (i.e. /:id)
 * and replaces them with values from the payload
 */
function buildRequest(url, payload) {
    // No payload passed, returning the plain url
    if (!payload) {
        return {
            url,
            payload: null
        };
    }

    let enrichedUrl = url;
    const filteredPayload = {};
    for (const payloadKey in payload) {
        const payloadValue = payload[payloadKey];

        const urlResourceParameterName = `:${payloadKey}`;
        // If the url contains a resource parameter then replace it with the corresponding payload's field
        if (enrichedUrl.includes(urlResourceParameterName)) {
            enrichedUrl = enrichedUrl.replace(urlResourceParameterName, encodeURIComponent(payloadValue));
        } else {
            filteredPayload[payloadKey] = payloadValue;
        }
    }

    return {
        url: enrichedUrl,
        payload: filteredPayload
    };
}

/**
 * Builds a query with the supplied url and query parameters
 */
function appendQueryParameters(url, queryParameters) {
    if (!queryParameters) {
        return url;
    }

    const queryString = Object.keys(queryParameters)
        .filter(queryParameterName => queryParameters[queryParameterName] != null)
        .map(queryParameterName => `${encodeURIComponent(queryParameterName)}=${encodeURIComponent(queryParameters[queryParameterName])}`)
        .join("&");

    if (!queryString) {
        return url;
    }

    return `${url}?${queryString}`;
}

function getRateLimiterLocalStorageRecordName(routeName, method) {
    return `rl:${method}:${routeName}`;
}

/**
 * Gets a timestamp until the supplied route is rate limited (if exists) from the local storage
 * Removes the timestamp if it exists but its value is less than now
 */
function getRateLimitedTillTimestamp(routeName, method) {
    const localStorageItemName = getRateLimiterLocalStorageRecordName(routeName, method);

    let rateLimitedTill = localStorage.getItem(localStorageItemName);
    if (!rateLimitedTill) {
        return null;
    }

    rateLimitedTill = Number(rateLimitedTill);
    if (moment().isAfter(rateLimitedTill)) {
        localStorage.removeItem(localStorageItemName);
        return null;
    }

    return rateLimitedTill;
}

/**
 * Saves the supplied route's "rate limited till" timestamp into the local storage
 */
function setRateLimitedTillTimestamp(routeName, method, rateLimitResetSeconds) {
    const localStorageItemName = getRateLimiterLocalStorageRecordName(routeName, method);
    const rateLimitedTillTimestamp = moment().add(rateLimitResetSeconds, "seconds").valueOf();
    localStorage.setItem(localStorageItemName, rateLimitedTillTimestamp);
}

/**
 * Submits a fetch with the supplied parameters
 * Parses the response into an object, logs out the user upon 401s
 */
async function request(routeName, url, method, payload, ignoreCache, on5xxAttemptsLeft = 3) {
    // Checking if the endpoint has been rate limited
    const rateLimitedTill = getRateLimitedTillTimestamp(routeName, method);
    if (rateLimitedTill) {
        const formattedDate = `${moment(rateLimitedTill).format("dddd, MMMM Do YYYY, h:mm:ss a")} (${moment(rateLimitedTill).fromNow()})`;

        console.error(`[api] ${method} ${routeName} is rate limited until ${formattedDate}`);

        // Throwing the same error as if it was an actuall api call that responeded with a 429
        throw new ApiError(`${method} ${routeName} endpoint is rate limited until ${formattedDate}`, 429);
    }

    const fetchParams = {
        method,
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
        },
        credentials: "same-origin"
    };

    if (payload) {
        fetchParams.body = JSON.stringify(payload);
    }

    // if we're signed-in, include the ID Token
    if (firebase.auth().currentUser) {
        const idToken = await firebase.auth().currentUser.getIdToken();
        fetchParams.headers["Authorization"] = `Bearer ${idToken}`;
    }

    if (ignoreCache) {
        fetchParams.cache = "reload";
    }

    const response = await fetch(url, fetchParams);

    if (response.status < 400) {
        return response.json();
    }

    if (response.status >= 400 && response.status < 500) {
        if (response.status === 429) {
            // Iterating through all X-RateLimit-Reset-<...> headers' values and fetching the biggest one
            let rateLimitResetSeconds = 0;
            const rateLimitResetHeaderRegex = /X-RateLimit-Reset/i;
            response.headers.forEach((headerValue, headerName) => {
                if (rateLimitResetHeaderRegex.test(headerName)) {
                    const numericHeaderValue = Number(headerValue);
                    if (numericHeaderValue > rateLimitResetSeconds) {
                        rateLimitResetSeconds = numericHeaderValue;
                    }
                }
            });

            // Saving the fetched value into the local storage, the error will be thrown by the code below
            setRateLimitedTillTimestamp(routeName, method, rateLimitResetSeconds);
        }

        let errorData;
        try {
            errorData = await response.json();
        } catch (err) {
            console.error(`Couldn't parse API response for ${url}, error:`, err);
        }

        if (errorData) {
            throw new ApiError(errorData.message, errorData.status, errorData.code);
        }

        throw new ApiError(`Api responded with ${response.status} status code`, response.status);
    }

    if (on5xxAttemptsLeft > 0) {
        console.log(`[api] ${method} ${routeName} sleeping for 1 second and retrying request, ${on5xxAttemptsLeft} attempts left`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return request(routeName, url, method, payload, ignoreCache, on5xxAttemptsLeft - 1);
    }

    throw new ApiError("Oops! Our server had an error. Try again later", response.status);
}

// Building the Api object from serverApi
for (const routeName in serverApi) {
    const routeUrl = serverApi[routeName];

    Api[routeName] = {
        get: (params, ignoreCache = false) => {
            const { url, payload } = buildRequest(routeUrl, params);
            return request(routeName, appendQueryParameters(url, payload), "GET", null, ignoreCache);
        },
        post: (params, rawPayload, ignoreCache = false) => {
            const { url, payload } = buildRequest(routeUrl, params);
            return request(routeName, url, "POST", rawPayload ? rawPayload : payload, ignoreCache);
        },
        put: (params, rawPayload, ignoreCache = false) => {
            const { url, payload } = buildRequest(routeUrl, params);
            return request(routeName, url, "PUT", rawPayload ? rawPayload : payload, ignoreCache);
        },
        delete: (params, rawPayload, ignoreCache = false) => {
            const { url, payload } = buildRequest(routeUrl, params);
            return request(routeName, url, "DELETE", rawPayload ? rawPayload : payload, ignoreCache);
        }
    };
}

export default Api;
